import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DoseEventRow, InjectionSite } from "@/lib/database.types";

/**
 * All logged doses for the patient, newest first. Powers the Shots page:
 * the estimated-levels chart, stat tiles, and the dose history list.
 * RLS (is_patient_owner) enforces that patients only see their own rows.
 */
export function useDoseEvents(patientId: string | undefined) {
  return useQuery<DoseEventRow[]>({
    queryKey: ["dose_events", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("dose_events")
        .select("*")
        .eq("patient_id", patientId!)
        .order("taken_at", { ascending: false });
      if (error) throw error;
      return (data as DoseEventRow[] | null) ?? [];
    },
  });
}

export interface LogDoseInput {
  medication: string;
  dose_mg: number;
  taken_at: string; // ISO timestamp
  injection_site: InjectionSite | null;
  pain_level: number | null;
  notes: string | null;
  treatment_id: string | null;
}

/**
 * Insert a dose the patient just administered. Direct table insert —
 * the RLS insert policy (is_patient_owner) is the authorization layer.
 */
export function useLogDose(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<DoseEventRow, Error, LogDoseInput>({
    mutationFn: async (input) => {
      if (!patientId) throw new Error("No patient");
      const { data, error } = await supabase
        .from("dose_events")
        .insert({ patient_id: patientId, ...input })
        .select("*")
        .single();
      if (error) throw error;
      return data as DoseEventRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dose_events", patientId] });
    },
  });
}
